import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Typography,
  Box,
  CircularProgress,
  FormControlLabel,
  Checkbox,
} from "@mui/material";
import axios from "axios";
import { setToast } from "../../../util/toast";

const ProductLimitDialog = ({ open, onClose, sellerData, onUpdate }) => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [limitInfo, setLimitInfo] = useState(null);
  const [newLimit, setNewLimit] = useState("");
  const [useGlobalDefault, setUseGlobalDefault] = useState(false);

  useEffect(() => {
    if (open && sellerData?._id) {
      fetchLimitInfo();
    }
  }, [open, sellerData]);

  const fetchLimitInfo = async () => {
    setLoading(true);
    try {
      const response = await axios.get(
        `seller/getProductLimitInfo?sellerId=${sellerData._id}`
      );
      if (response.data.status) {
        setLimitInfo(response.data.data);
        setNewLimit(
          response.data.data.isCustomLimit
            ? response.data.data.productLimit
            : ""
        );
        setUseGlobalDefault(!response.data.data.isCustomLimit);
      }
    } catch (error) {
      console.error("Error fetching limit info:", error);
      setToast("error", "Failed to fetch product limit info");
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const response = await axios.patch(
        `seller/updateProductLimit?sellerId=${sellerData._id}`,
        {
          productLimit: useGlobalDefault ? null : parseInt(newLimit) || 0,
        }
      );
      if (response.data.status) {
        setToast("success", "Product limit updated successfully");
        if (onUpdate) onUpdate(response.data.data);
        onClose();
      } else {
        setToast("error", response.data.message);
      }
    } catch (error) {
      console.error("Error updating limit:", error);
      setToast("error", "Failed to update product limit");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ backgroundColor: "#1a1a2e", color: "#fff" }}>
        Product Upload Limit - {sellerData?.firstName} {sellerData?.lastName}
      </DialogTitle>
      <DialogContent sx={{ backgroundColor: "#1a1a2e", color: "#fff", pt: 2 }}>
        {loading ? (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress sx={{ color: "#F0C420" }} />
          </Box>
        ) : (
          <Box>
            {/* Current Status */}
            <Box
              sx={{
                backgroundColor: "#252540",
                borderRadius: 2,
                p: 2,
                mb: 3,
              }}
            >
              <Typography variant="subtitle2" color="#888" gutterBottom>
                Current Status
              </Typography>
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
              >
                <Typography>Products Uploaded:</Typography>
                <Typography fontWeight="bold" color="#F0C420">
                  {limitInfo?.currentCount || 0}
                </Typography>
              </Box>
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                mt={1}
              >
                <Typography>Current Limit:</Typography>
                <Typography fontWeight="bold" color="#F0C420">
                  {limitInfo?.isUnlimited
                    ? "Unlimited"
                    : limitInfo?.productLimit || 0}
                </Typography>
              </Box>
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                mt={1}
              >
                <Typography>Remaining:</Typography>
                <Typography fontWeight="bold" color="#4caf50">
                  {limitInfo?.isUnlimited
                    ? "Unlimited"
                    : limitInfo?.remaining || 0}
                </Typography>
              </Box>
            </Box>

            {/* Settings */}
            <Box>
              <Typography variant="subtitle2" color="#888" gutterBottom>
                Update Limit
              </Typography>

              <FormControlLabel
                control={
                  <Checkbox
                    checked={useGlobalDefault}
                    onChange={(e) => {
                      setUseGlobalDefault(e.target.checked);
                      if (e.target.checked) setNewLimit("");
                    }}
                    sx={{
                      color: "#F0C420",
                      "&.Mui-checked": { color: "#F0C420" },
                    }}
                  />
                }
                label="Use global default limit"
                sx={{ color: "#fff", mb: 2 }}
              />

              {!useGlobalDefault && (
                <TextField
                  label="Custom Product Limit"
                  type="number"
                  fullWidth
                  value={newLimit}
                  onChange={(e) => setNewLimit(e.target.value)}
                  placeholder="Enter limit (0 for unlimited)"
                  InputProps={{
                    sx: { color: "#fff" },
                  }}
                  InputLabelProps={{
                    sx: { color: "#888" },
                  }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      "& fieldset": { borderColor: "#444" },
                      "&:hover fieldset": { borderColor: "#F0C420" },
                      "&.Mui-focused fieldset": { borderColor: "#F0C420" },
                    },
                  }}
                  helperText="Set to 0 for unlimited products"
                  FormHelperTextProps={{ sx: { color: "#666" } }}
                />
              )}
            </Box>
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ backgroundColor: "#1a1a2e", p: 2 }}>
        <Button onClick={onClose} sx={{ color: "#888" }}>
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          disabled={saving || loading}
          sx={{
            backgroundColor: "#F0C420",
            color: "#000",
            "&:hover": { backgroundColor: "#d4a818" },
          }}
        >
          {saving ? "Saving..." : "Save"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ProductLimitDialog;
